export const normalizeCountryCode = (code) => (code || "").trim().toUpperCase();

export const isUsCountry = (code) => normalizeCountryCode(code) === "US";
export const isBrCountry = (code) => normalizeCountryCode(code) === "BR";

export const BAAS_US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC",
];

export const BAAS_BR_STATES = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

export const getStateOptions = (countryCode) => {
  if (isUsCountry(countryCode)) {
    return BAAS_US_STATES;
  }
  if (isBrCountry(countryCode)) {
    return BAAS_BR_STATES;
  }
  return [];
};

/**
 * Returns which form fields should be visible based on country and owner context.
 * @param {import('../../../../Services/baasBusinessAccountApi').EMPTY_BUSINESS_CREATE_FORM} form
 * @param {object} [owner]
 */
export const getVisibleFields = (form, owner = form?.beneficialOwners?.[0]) => {
  const country = form?.countryOfIncorporation;
  const hasCountry = Boolean(normalizeCountryCode(country));
  const isUs = isUsCountry(country);
  const isBr = isBrCountry(country);

  return {
    // No country yet → hide EIN/registration so Documento stays country + number only.
    ein: hasCountry && isUs,
    einPending: hasCountry && isUs,
    registrationId: hasCountry && !isUs,
    stateOfIncorporationSelect: isUs || isBr,
    stateOfIncorporationText: hasCountry && !isUs && !isBr,
    ssn:
      isUsCountry(owner?.countryOfCitizenship) || isUsCountry(owner?.address?.countryCode),
  };
};

export const hasIdentityDocData = (doc) =>
  Boolean(doc?.number?.trim() || doc?.countryCode?.trim());

export const hasIdentityDocNumber = (doc) => Boolean(doc?.number?.trim());

export const isIdentityDocComplete = (doc) =>
  Boolean(doc?.number?.trim() && doc?.countryCode?.trim());

export const hasAnyOwnerIdentityDocNumber = (owner) =>
  hasIdentityDocNumber(owner?.passport) ||
  hasIdentityDocNumber(owner?.driversLicense) ||
  hasIdentityDocNumber(owner?.nationalId);

export const hasAnyCompleteOwnerIdentityDoc = (owner) =>
  isIdentityDocComplete(owner?.passport) ||
  isIdentityDocComplete(owner?.driversLicense) ||
  isIdentityDocComplete(owner?.nationalId);

const ENTIDADE_DRAFT_KEY_PREFIX = "baas-entidade-draft-";

export const getEntidadeDraftStorageKey = (instanceId) =>
  `${ENTIDADE_DRAFT_KEY_PREFIX}${instanceId || "default"}`;

/** @param {string} instanceId @param {{ form: object, stepIndex: number }} draft */
export const saveEntidadeDraft = (instanceId, draft) => {
  if (!instanceId) {
    return;
  }
  try {
    sessionStorage.setItem(
      getEntidadeDraftStorageKey(instanceId),
      JSON.stringify({ ...draft, savedAt: Date.now() }),
    );
  } catch {
    // sessionStorage may be unavailable or full
  }
};

/** @param {string} instanceId @returns {{ form: object, stepIndex: number } | null} */
export const loadEntidadeDraft = (instanceId) => {
  if (!instanceId) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(getEntidadeDraftStorageKey(instanceId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed?.form || typeof parsed.stepIndex !== "number") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/** @param {string} instanceId */
export const clearEntidadeDraft = (instanceId) => {
  if (!instanceId) {
    return;
  }
  try {
    sessionStorage.removeItem(getEntidadeDraftStorageKey(instanceId));
  } catch {
    // ignore
  }
};
